/**
 * Inline expandable BOM editor for a single Bidso SKU under review.
 *
 * Wires to:
 *   GET  /api/bidso-composer/{id}/bom-detail
 *   PUT  /api/bidso-composer/{id}/bom-items       (Tech Ops edit, atomic replace)
 *   GET  /api/raw-materials/search?q=...&limit=20 (RM repository picker)
 *   POST /api/bidso-composer/{id}/approve
 *   POST /api/bidso-composer/{id}/send-back        { remarks }
 *
 * Rules enforced in the UI:
 *   • Tech Ops cannot edit the pantone of an existing row (read-only display).
 *   • Tech Ops cannot edit the unit of an existing row.
 *   • Tech Ops CAN edit quantity, remove a row, or add a new row by picking
 *     from the RM repository (no new RM creation from here).
 *   • If the user has `is_self_submit=true` (and is not master_admin), the
 *     entire editor stays collapsed; only the lock badge shows.
 *   • Approve / Send back save any pending BOM edits first (via PUT), then
 *     transition the status. The diff summary is appended to the send-back
 *     remarks so the designer sees what changed.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Check, X, Search, Lock, AlertTriangle, ChevronDown, ChevronUp, Plus, Trash2, Pencil, Loader2, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BidsoReviewRow({ row, token, onActionDone }) {
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const isSelfSubmit = row.is_self_submit;
  const isPending = row.status === "PENDING_REVIEW";
  const isSentBack = row.status === "SENT_BACK";

  // ----- Expansion + BOM load -----
  const [expanded, setExpanded] = useState(false);
  const [bomLoading, setBomLoading] = useState(false);
  const [bomItems, setBomItems] = useState([]);          // editable working copy (only PENDING)
  const addedSeq = useRef(0);
  const [originalItems, setOriginalItems] = useState([]); // snapshot for dirty detection

  // ----- RM picker (Add row) -----
  const [rmPickerOpen, setRmPickerOpen] = useState(false);
  const [rmQuery, setRmQuery] = useState("");
  const [rmResults, setRmResults] = useState([]);
  const [rmSearching, setRmSearching] = useState(false);

  // ----- Action state -----
  const [approving, setApproving] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackRemarks, setSendBackRemarks] = useState("");
  const [sendBackSubmitting, setSendBackSubmitting] = useState(false);
  // Feb 2026 — Tech Ops third decision: reject-and-scrap. Ends the
  // submission, wipes the composition, returns the SKU code to the
  // "needs BOM" pool. Same bom_locked refusal as designer scrap.
  const [rejectScrapOpen, setRejectScrapOpen] = useState(false);
  const [rejectScrapRemarks, setRejectScrapRemarks] = useState("");
  const [rejectScrapSubmitting, setRejectScrapSubmitting] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    (async () => {
      setBomLoading(true);
      try {
        const res = await axios.get(
          `${API}/bidso-composer/${encodeURIComponent(row.bidso_sku_id)}/bom-detail`,
          { headers: authHeaders },
        );
        // Client-only `_row_key` identifies each row; `rm_id` is not unique when
        // the BOM carries a duplicate RM line. Never sent to the server.
        const items = (res.data?.items || []).map((it, i) => ({ ...it, _row_key: `row-${i}` }));
        setBomItems(items);
        setOriginalItems(JSON.parse(JSON.stringify(items)));
      } catch (e) {
        toast.error("Failed to load BOM detail");
      } finally {
        setBomLoading(false);
      }
    })();
  }, [expanded, row.bidso_sku_id]);

  // Dirty check: did the user actually change anything?
  const isDirty = (() => {
    if (bomItems.length !== originalItems.length) return true;
    const oIdx = new Map(originalItems.map((i) => [i._row_key, i]));
    for (const it of bomItems) {
      const orig = oIdx.get(it._row_key);
      if (!orig) return true;
      if (Number(orig.quantity) !== Number(it.quantity)) return true;
    }
    return false;
  })();

  // ---- RM search (debounced) ----
  const [rmDebouncedQuery, setRmDebouncedQuery] = useState("");
  useEffect(() => {
    const handle = setTimeout(() => setRmDebouncedQuery(rmQuery.trim()), 250);
    return () => clearTimeout(handle);
  }, [rmQuery]);

  useEffect(() => {
    (async () => {
      if (!rmPickerOpen) return;
      if (rmDebouncedQuery.length < 2) {
        setRmResults([]);
        return;
      }
      setRmSearching(true);
      try {
        const res = await axios.get(
          `${API}/raw-materials/search?q=${encodeURIComponent(rmDebouncedQuery)}&limit=20`,
          { headers: authHeaders },
        );
        const existing = new Set(bomItems.map((i) => i.rm_id));
        // Backend search endpoint returns { items: [{ item_id, name, category, ... }] };
        // normalise into the rm_id/rm_name shape this component uses everywhere
        // else so dedup, React keys, testids and downstream payloads all line up.
        const normalised = (res.data?.items || []).map((r) => ({
          ...r,
          rm_id: r.item_id ?? r.rm_id,
          rm_name: r.rm_name || r.description || r.name || "",
        }));
        setRmResults(normalised.filter((r) => r.rm_id && !existing.has(r.rm_id)));
      } catch (e) {
        setRmResults([]);
      } finally {
        setRmSearching(false);
      }
    })();
  }, [rmDebouncedQuery, rmPickerOpen]);

  const handleAddRm = (rm) => {
    setBomItems((prev) => [
      ...prev,
      {
        rm_id: rm.rm_id,
        rm_name: rm.rm_name || rm.description || rm.name || "",
        category: rm.category || "—",
        uom: rm.uom || null,
        part_name: "",
        mould_code: "",
        pantone_code: "",
        quantity: 1,
        unit: rm.uom || "nos",
        _added_by_tech_ops: true,
        _row_key: `added-${addedSeq.current++}`,
      },
    ]);
    setRmPickerOpen(false);
    setRmQuery("");
  };

  const handleRemoveRow = (rowKey) => {
    setBomItems((prev) => prev.filter((it) => it._row_key !== rowKey));
  };

  const handleQtyChange = (rowKey, value) => {
    const n = Number(value);
    if (Number.isNaN(n) || n <= 0) return;
    setBomItems((prev) => prev.map((it) => (it._row_key === rowKey ? { ...it, quantity: n } : it)));
  };

  // Unique testid suffix per row: first occurrence keeps `<rm_id>`, later
  // duplicates of the same rm_id get `<rm_id>-dup1`, `-dup2`, ...
  const rowTestIds = (() => {
    const seen = {};
    return bomItems.map((it) => {
      const n = seen[it.rm_id] || 0;
      seen[it.rm_id] = n + 1;
      return n === 0 ? it.rm_id : `${it.rm_id}-dup${n}`;
    });
  })();

  // Persist BOM edits via PUT /bom-items. Returns the diff_summary string on
  // success, or null if there was nothing to save (no edits). Throws on error.
  const persistBomEditsIfDirty = async () => {
    if (!isDirty) return null;
    const payload = {
      items: bomItems.map((it) => ({
        rm_id: it.rm_id,
        quantity: Number(it.quantity) || 1,
        unit: it.unit || "nos",
      })),
    };
    const res = await axios.put(
      `${API}/bidso-composer/${encodeURIComponent(row.bidso_sku_id)}/bom-items`,
      payload,
      { headers: authHeaders },
    );
    return res.data?.diff_summary || null;
  };

  const handleApprove = async () => {
    if (approving) return;
    setApproving(true);
    try {
      const diff = await persistBomEditsIfDirty();
      await axios.post(
        `${API}/bidso-composer/${encodeURIComponent(row.bidso_sku_id)}/approve`,
        {},
        { headers: authHeaders },
      );
      toast.success(
        diff
          ? `${row.bidso_sku_id} approved with edits (${diff}) — now ACTIVE`
          : `${row.bidso_sku_id} approved — now ACTIVE`,
        { duration: 6000 },
      );
      onActionDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "DUPLICATE_BOM") {
        toast.error(
          `After your edits, this BOM matches ACTIVE SKU ${d.matched_bidso_sku_id}. Revert or adjust.`,
          { duration: 9000 },
        );
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Approve failed"));
      }
    } finally {
      setApproving(false);
    }
  };

  const handleOpenSendBack = () => {
    setSendBackRemarks("");
    setSendBackOpen(true);
  };

  const submitSendBack = async () => {
    const remarks = sendBackRemarks.trim();
    if (remarks.length < 3) {
      toast.error("Remarks must be at least 3 characters");
      return;
    }
    setSendBackSubmitting(true);
    try {
      const diff = await persistBomEditsIfDirty();
      // Append the diff to the remarks so the designer sees both narrative + machine diff.
      const finalRemarks = diff
        ? `${remarks}\n\n[Tech Ops BOM edits: ${diff}]`
        : remarks;
      await axios.post(
        `${API}/bidso-composer/${encodeURIComponent(row.bidso_sku_id)}/send-back`,
        { remarks: finalRemarks },
        { headers: authHeaders },
      );
      toast.success(`${row.bidso_sku_id} sent back to submitter`);
      setSendBackOpen(false);
      onActionDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.error === "DUPLICATE_BOM") {
        toast.error(`After your edits, this BOM matches ACTIVE SKU ${d.matched_bidso_sku_id}.`, { duration: 9000 });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Send-back failed"));
      }
    } finally {
      setSendBackSubmitting(false);
    }
  };

  const handleOpenRejectScrap = () => {
    setRejectScrapRemarks("");
    setRejectScrapOpen(true);
  };

  // Feb 2026 — Third Tech Ops decision. Wipes composition + DRAFT RMs
  // (safe, reuse-skipped), returns SKU to DRAFT_NO_BOM. Same 409
  // bom_locked refusal as the designer scrap-and-delete. Does NOT
  // persist pending BOM edits — the whole point is to abandon them.
  const submitRejectScrap = async () => {
    const remarks = rejectScrapRemarks.trim();
    if (remarks.length < 3) {
      toast.error("Remarks must be at least 3 characters");
      return;
    }
    setRejectScrapSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/bidso-composer/${encodeURIComponent(row.bidso_sku_id)}/reject-and-scrap`,
        { remarks },
        { headers: authHeaders },
      );
      const deleted = (r.data?.draft_rms_deleted || []).length;
      const skipped = (r.data?.draft_rms_skipped_reused || []).length;
      toast.success(
        `${row.bidso_sku_id} rejected & scrapped — ${deleted} DRAFT part(s) removed`
        + (skipped ? `, ${skipped} kept (reused elsewhere)` : "")
        + ". SKU code returned to the needs-BOM pool.",
      );
      setRejectScrapOpen(false);
      onActionDone?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "BUYER_SKUS_BOM_LOCKED") {
        toast.error(d.message || "Refused: downstream buyer SKUs are bom_locked.", { duration: 9000 });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Reject & scrap failed"));
      }
    } finally {
      setRejectScrapSubmitting(false);
    }
  };

  const toggleExpansion = () => {
    if (isSelfSubmit) {
      toast.message("You submitted this Bidso SKU — only another Tech Ops user can review it.");
      return;
    }
    setExpanded((v) => !v);
  };

  return (
    <Card
      className={isSentBack ? "border-red-200 bg-red-50/30" : "border-amber-200 bg-amber-50/30"}
      data-testid={`review-row-${row.bidso_sku_id}`}
    >
      <CardHeader
        className="pb-3 cursor-pointer hover:bg-black/[0.02] transition-colors"
        onClick={toggleExpansion}
        data-testid={`review-row-header-${row.bidso_sku_id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {isSelfSubmit ? null : (
                expanded
                  ? <ChevronUp className="w-4 h-4 text-zinc-500"/>
                  : <ChevronDown className="w-4 h-4 text-zinc-500"/>
              )}
              <Badge variant="outline" className="font-mono">{row.bidso_sku_id}</Badge>
              <CardTitle className="text-base">{row.name || "—"}</CardTitle>
              {isPending && <Badge className="bg-amber-100 text-amber-800 border-amber-200 font-mono text-[10px]">PENDING_REVIEW</Badge>}
              {isSentBack && <Badge className="bg-red-100 text-red-800 border-red-200 font-mono text-[10px]">SENT_BACK</Badge>}
              {row.tech_ops_modified_at && (
                <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[10px]" title={row.tech_ops_edit_summary || ""}>
                  <Pencil className="w-3 h-3 mr-1"/>Tech Ops edited
                </Badge>
              )}
            </div>
            <CardDescription className="mt-2 text-xs font-mono space-x-3">
              {row.vertical_code && <span>{row.vertical_code} / {row.model_code}{row.license_code ? ` / ${row.license_code}` : ""}</span>}
              <span>· Submitted by <span className="font-semibold">{row.submitted_by_name || "—"}</span></span>
              {row.submitted_at && <span>· {new Date(row.submitted_at).toLocaleString()}</span>}
              {row.source_bidso_sku_id && (
                <span>· Cloned from <span className="font-semibold">{row.source_bidso_sku_id}</span></span>
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {isSelfSubmit && (
              <Badge variant="outline" className="text-[10px] flex items-center gap-1 border-zinc-300 text-zinc-600">
                <Lock className="w-3 h-3"/>You submitted this
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>

      {/* Existing SENT_BACK remarks visible always */}
      {isSentBack && row.review_remarks && (
        <CardContent className="pt-0">
          <div className="rounded-md border border-red-200 bg-white p-3 flex items-start gap-2" data-testid={`review-remarks-${row.bidso_sku_id}`}>
            <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5"/>
            <div className="text-sm">
              <div className="font-semibold text-red-900">Sent-back remarks:</div>
              <div className="text-red-800 mt-0.5 whitespace-pre-wrap">{row.review_remarks}</div>
              {row.reviewed_by_name && (
                <div className="text-xs text-red-600 mt-1 font-mono">
                  by {row.reviewed_by_name}{row.reviewed_at ? ` · ${new Date(row.reviewed_at).toLocaleString()}` : ""}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      )}

      {/* Designer-to-Tech-Ops note attached at submission time. Always visible
          when present, regardless of expansion state, so Tech Ops sees the
          context immediately. */}
      {row.submitter_remarks && (
        <CardContent className="pt-0">
          <div
            className="rounded-md border border-violet-200 bg-violet-50 p-3 flex items-start gap-2"
            data-testid={`submitter-remarks-${row.bidso_sku_id}`}
          >
            <MessageSquare className="w-4 h-4 text-violet-600 flex-shrink-0 mt-0.5"/>
            <div className="text-sm">
              <div className="font-semibold text-violet-900">Designer note:</div>
              <div className="text-violet-900 mt-0.5 whitespace-pre-wrap">{row.submitter_remarks}</div>
              {row.submitted_by_name && (
                <div className="text-xs text-violet-700 mt-1 font-mono">
                  from {row.submitted_by_name}{row.submitted_at ? ` · ${new Date(row.submitted_at).toLocaleString()}` : ""}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      )}

      {/* ----- Expanded BOM editor ----- */}
      {expanded && !isSelfSubmit && (
        <CardContent className="pt-0" data-testid={`review-editor-${row.bidso_sku_id}`}>
          <div className="rounded-md border bg-white p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-zinc-900">BOM under review</div>
                <div className="text-xs text-zinc-500">
                  Edit quantities, remove rows, or add RMs from the repository. You cannot change pantones —
                  send back if a colour choice is wrong.
                </div>
              </div>
              {isPending && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRmPickerOpen(true)}
                  data-testid={`add-rm-btn-${row.bidso_sku_id}`}
                >
                  <Plus className="w-4 h-4 mr-1"/>Add RM
                </Button>
              )}
            </div>

            {bomLoading ? (
              <div className="py-8 text-center text-sm text-zinc-500">
                <Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading BOM…
              </div>
            ) : bomItems.length === 0 ? (
              <div className="py-8 text-center text-sm text-zinc-500">
                BOM is empty. Add at least one RM before approving.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`bom-table-${row.bidso_sku_id}`}>
                  <thead>
                    <tr className="border-b text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
                      <th className="text-left py-2 px-2">RM ID</th>
                      <th className="text-left py-2 px-2">Description</th>
                      <th className="text-left py-2 px-2">Category</th>
                      <th className="text-left py-2 px-2">Pantone</th>
                      <th className="text-right py-2 px-2 w-24">Qty</th>
                      <th className="text-left py-2 px-2 w-16">Unit</th>
                      {isPending && <th className="w-10 py-2 px-2"/>}
                    </tr>
                  </thead>
                  <tbody>
                    {bomItems.map((it, idx) => (
                      <tr
                        key={it._row_key}
                        className={`border-b last:border-b-0 ${it._added_by_tech_ops ? "bg-violet-50/50" : ""}`}
                        data-testid={`bom-row-${row.bidso_sku_id}-${rowTestIds[idx]}`}
                      >
                        <td className="py-2 px-2">
                          <Badge variant="outline" className="font-mono text-xs">{it.rm_id}</Badge>
                          {it._added_by_tech_ops && (
                            <Badge className="ml-1 bg-violet-100 text-violet-800 border-violet-200 text-[10px]">added</Badge>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <div className="text-sm font-medium text-zinc-900">{it.rm_name || "—"}</div>
                          {it.part_name && it.mould_code && (
                            <div className="text-xs text-zinc-500 font-mono">{it.part_name} · {it.mould_code}</div>
                          )}
                        </td>
                        <td className="py-2 px-2">
                          <Badge variant="outline" className="font-mono text-[10px]">{it.category}</Badge>
                        </td>
                        <td className="py-2 px-2 font-mono text-xs text-zinc-700">
                          {it.pantone_code || <span className="text-zinc-400">—</span>}
                        </td>
                        <td className="py-2 px-2 text-right">
                          {isPending ? (
                            <Input
                              type="number"
                              min="0.001"
                              step="0.001"
                              value={it.quantity}
                              onChange={(e) => handleQtyChange(it._row_key, e.target.value)}
                              className="h-8 w-20 ml-auto text-right"
                              data-testid={`bom-qty-${row.bidso_sku_id}-${rowTestIds[idx]}`}
                            />
                          ) : (
                            <span className="text-sm">{it.quantity}</span>
                          )}
                        </td>
                        <td className="py-2 px-2 text-xs font-mono text-zinc-600">{it.unit}</td>
                        {isPending && (
                          <td className="py-2 px-2 text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-red-600 hover:bg-red-100"
                              onClick={() => handleRemoveRow(it._row_key)}
                              data-testid={`bom-remove-${row.bidso_sku_id}-${rowTestIds[idx]}`}
                              title="Remove row"
                            >
                              <Trash2 className="w-3.5 h-3.5"/>
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Dirty indicator */}
            {isPending && isDirty && (
              <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-xs text-violet-900 flex items-center gap-2" data-testid={`dirty-indicator-${row.bidso_sku_id}`}>
                <Pencil className="w-3.5 h-3.5"/>
                You have unsaved BOM edits. Clicking <span className="font-semibold">Approve</span> or <span className="font-semibold">Send back</span> below will save them automatically.
              </div>
            )}

            {/* Tech Ops history if previously edited */}
            {row.tech_ops_modified_at && row.tech_ops_edit_summary && (
              <div className="rounded-md border bg-zinc-50 px-3 py-2 text-xs text-zinc-700 font-mono" data-testid={`tech-ops-history-${row.bidso_sku_id}`}>
                Previous Tech Ops edit ({new Date(row.tech_ops_modified_at).toLocaleString()} by {row.tech_ops_modified_by_name}):
                <span className="ml-2 text-zinc-900">{row.tech_ops_edit_summary}</span>
              </div>
            )}

            {/* Actions */}
            {isPending && (
              <div className="flex flex-wrap justify-end gap-2 pt-3 border-t">
                <Button
                  variant="outline"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={handleOpenRejectScrap}
                  disabled={approving || sendBackSubmitting}
                  data-testid={`reject-scrap-btn-${row.bidso_sku_id}`}
                  title="End this submission — wipe the composition and return the SKU code to the needs-BOM pool"
                >
                  <Trash2 className="w-4 h-4 mr-1"/>Reject &amp; scrap
                </Button>
                <Button
                  variant="outline"
                  className="border-red-200 text-red-700 hover:bg-red-50"
                  onClick={handleOpenSendBack}
                  disabled={approving}
                  data-testid={`send-back-btn-${row.bidso_sku_id}`}
                >
                  <X className="w-4 h-4 mr-1"/>{isDirty ? "Save & Send back" : "Send back"}
                </Button>
                <Button
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleApprove}
                  disabled={approving || bomItems.length === 0}
                  data-testid={`approve-btn-${row.bidso_sku_id}`}
                >
                  {approving ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Check className="w-4 h-4 mr-1"/>}
                  {approving ? "Approving…" : isDirty ? "Save & Approve" : "Approve"}
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      )}

      {/* ----- RM picker dialog ----- */}
      <Dialog open={rmPickerOpen} onOpenChange={setRmPickerOpen}>
        <DialogContent className="max-w-2xl" data-testid={`rm-picker-${row.bidso_sku_id}`}>
          <DialogHeader>
            <DialogTitle>Add an RM to this BOM</DialogTitle>
            <DialogDescription>
              Search the RM repository by code or description. You cannot create a new RM from here —
              if the part doesn&apos;t exist, ask the RM team to create it first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400"/>
              <Input
                autoFocus
                value={rmQuery}
                onChange={(e) => setRmQuery(e.target.value)}
                placeholder="Type at least 2 characters…"
                className="pl-9"
                data-testid="rm-picker-search"
              />
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-md">
              {rmSearching ? (
                <div className="py-6 text-center text-sm text-zinc-500">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2"/>Searching…
                </div>
              ) : rmQuery.trim().length < 2 ? (
                <div className="py-6 text-center text-xs text-zinc-400">Start typing to search</div>
              ) : rmResults.length === 0 ? (
                <div className="py-6 text-center text-sm text-zinc-500" data-testid="rm-picker-empty">
                  No matching RM found.
                </div>
              ) : (
                <ul className="divide-y">
                  {rmResults.map((r) => (
                    <li
                      key={r.rm_id}
                      className="px-3 py-2 hover:bg-violet-50 cursor-pointer flex items-center justify-between gap-3"
                      onClick={() => handleAddRm(r)}
                      data-testid={`rm-picker-result-${r.rm_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono text-xs">{r.rm_id}</Badge>
                          <Badge variant="outline" className="font-mono text-[10px]">{r.category || "—"}</Badge>
                        </div>
                        <div className="text-sm mt-0.5 truncate">{r.rm_name || r.description || r.name}</div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-violet-600 hover:bg-violet-100">
                        <Plus className="w-4 h-4"/>
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRmPickerOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ----- Send-back dialog ----- */}
      <Dialog open={sendBackOpen} onOpenChange={(o) => !o && setSendBackOpen(false)}>
        <DialogContent className="max-w-lg" data-testid={`send-back-dialog-${row.bidso_sku_id}`}>
          <DialogHeader>
            <DialogTitle>Send back to submitter</DialogTitle>
            <DialogDescription asChild>
              <div>
                <span className="font-mono font-semibold">{row.bidso_sku_id}</span>
                {" "}will move to <Badge className="bg-red-100 text-red-800 border-red-200 font-mono text-[10px]">SENT_BACK</Badge>.
                {isDirty && (
                  <span className="block mt-1 text-violet-700">
                    Your BOM edits will be saved and the diff appended to the remarks so the designer sees what changed.
                  </span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider font-mono text-zinc-600">
              Remarks (required, min 3 chars)
            </label>
            <Textarea
              value={sendBackRemarks}
              onChange={(e) => setSendBackRemarks(e.target.value)}
              placeholder="e.g. ACC_024 is wrong polymer for this model — replaced with ACC_055. Please verify."
              rows={4}
              data-testid={`send-back-remarks-${row.bidso_sku_id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendBackOpen(false)} disabled={sendBackSubmitting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submitSendBack}
              disabled={sendBackSubmitting || sendBackRemarks.trim().length < 3}
              data-testid={`send-back-confirm-${row.bidso_sku_id}`}
            >
              {sendBackSubmitting ? "Sending…" : "Send back"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ----- Reject & scrap dialog (Tech Ops third decision, Feb 2026) ----- */}
      <Dialog open={rejectScrapOpen} onOpenChange={(o) => !o && setRejectScrapOpen(false)}>
        <DialogContent className="max-w-lg" data-testid={`reject-scrap-dialog-${row.bidso_sku_id}`}>
          <DialogHeader>
            <DialogTitle>Reject &amp; scrap this submission</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2">
                <div>
                  <span className="font-mono font-semibold">{row.bidso_sku_id}</span>
                  {" "}will end this submission — its <span className="font-semibold">common BOM will be deleted</span>,
                  any DRAFT raw materials this SKU minted will be safe-deleted
                  (skipping any that another SKU still uses), and the SKU code
                  returns to the &ldquo;needs BOM&rdquo; pool as a fresh
                  <Badge className="ml-1 bg-zinc-100 text-zinc-800 border-zinc-200 font-mono text-[10px]">DRAFT_NO_BOM</Badge>.
                </div>
                <div className="rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0"/>
                  <span>
                    Any pending BOM edits above will be discarded. If a downstream buyer SKU is
                    {" "}<span className="font-mono">bom_locked</span>, this will be refused.
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wider font-mono text-zinc-600">
              Remarks (required, min 3 chars)
            </label>
            <Textarea
              value={rejectScrapRemarks}
              onChange={(e) => setRejectScrapRemarks(e.target.value)}
              placeholder="e.g. Wrong Bidso SKU code — designer to re-pick a fresh code."
              rows={4}
              data-testid={`reject-scrap-remarks-${row.bidso_sku_id}`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectScrapOpen(false)} disabled={rejectScrapSubmitting}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={submitRejectScrap}
              disabled={rejectScrapSubmitting || rejectScrapRemarks.trim().length < 3}
              data-testid={`reject-scrap-confirm-${row.bidso_sku_id}`}
            >
              {rejectScrapSubmitting ? "Scrapping…" : "Reject & scrap"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
